<template>
  <div class="space-y-4">
    <div>
      <Label class="font-medium">Interviewer</Label>
      <Select v-model="interviewerId">
        <SelectTrigger class="mt-2">
          <span v-if="selectedInterviewerLabel">{{ selectedInterviewerLabel }}</span>
          <SelectValue v-else placeholder="Select interviewer" />
        </SelectTrigger>
        <SelectContent>
          <SelectLabel v-if="showSuggestedLabel" class="px-2 pt-1.5 pb-1 text-xs font-medium text-muted-foreground">
            Suggested
          </SelectLabel>
          <RekaSelectItem
            v-for="option in interviewerOptions"
            :key="option.value"
            :value="option.value"
            class="relative flex w-full cursor-default select-none rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground"
          >
            <span class="absolute left-2 top-2 flex items-center justify-center">
              <SelectItemIndicator>
                <Check class="h-4 w-4" />
              </SelectItemIndicator>
            </span>
            <div class="flex flex-col gap-1">
              <div>
                {{ option.label }}
                <span class="text-muted-foreground text-xs ml-1">{{ option.email }}</span>
              </div>
              <div v-if="option.assignments.length" class="flex flex-wrap gap-1">
                <Badge v-for="assignment in option.assignments" :key="assignment" variant="outline" class="text-xs">
                  Assigned To {{ assignment }}
                </Badge>
              </div>
            </div>
          </RekaSelectItem>
          <template v-if="!isExpanded">
            <SelectSeparator v-if="interviewerOptions.length" />
            <button
              type="button"
              :disabled="isLoadingMore"
              class="flex w-full cursor-default select-none items-center gap-2.5 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground disabled:cursor-progress disabled:opacity-70"
              @click.stop.prevent="loadMoreInterviewers"
              @pointerdown.stop.prevent
              @keydown.space.stop.prevent="loadMoreInterviewers"
              @keydown.enter.stop.prevent="loadMoreInterviewers"
            >
              <span v-if="isLoadingMore" class="spinner h-4 w-4 shrink-0" aria-hidden="true" />
              <Users v-else class="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span class="flex flex-col leading-tight">
                <span class="font-medium">
                  {{ isLoadingMore ? 'Checking calendars…' : 'Show all team members' }}
                </span>
                <span class="text-xs text-muted-foreground">
                  {{
                    isLoadingMore ? 'This can take a few seconds.' : 'Only members with a connected calendar can host.'
                  }}
                </span>
              </span>
            </button>
          </template>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label class="font-medium">Date & Time</Label>
      <DatePicker v-model="dateTime" granularity="minute" disable-past class="mt-2" />
    </div>
    <div>
      <Label class="font-medium">Duration</Label>
      <Select v-model="duration">
        <SelectTrigger class="mt-2">
          <SelectValue placeholder="Select duration" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="option in durationOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label class="font-medium">Location</Label>
      <Select v-model="location">
        <SelectTrigger class="mt-2">
          <SelectValue placeholder="Select location" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="option in locationOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
      <Alert v-if="location === 'standard_conferencing' && calendarProvider" variant="info" class="mt-2">
        <Video class="h-4 w-4" />
        <AlertDescription> A {{ calendarProvider }} link will be added to the calendar event. </AlertDescription>
      </Alert>
      <AddressInput v-if="location === 'physical_location'" v-model="address" label="Address" class="mt-2 space-y-1" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { DatePicker } from '@/components/ui/date_picker';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SelectItem as RekaSelectItem, SelectItemIndicator } from 'reka-ui';
import { Check, Users, Video } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { useUserCalendarStore } from '@/stores/userCalendar';
import AddressInput from '@/components/shared/AddressInput.vue';
import type { UseAdminScheduleInputReturn } from './useAdminScheduleInput';

type Props = {
  input: UseAdminScheduleInputReturn;
};

defineEmits<{ back: [] }>();

const props = defineProps<Props>();
const {
  dateTime,
  location,
  address,
  duration,
  interviewerId,
  interviewerOptions,
  locationOptions,
  durationOptions,
  isExpanded,
  isLoadingMore,
  loadMoreInterviewers,
} = props.input;

const userCalendarStore = useUserCalendarStore();
const calendarProvider = computed(() => {
  const { isGoogleAuthenticated, isMicrosoftAuthenticated } = userCalendarStore.currentUserCalendar;
  if (isGoogleAuthenticated) return 'Google Meet';
  if (isMicrosoftAuthenticated) return 'Microsoft Teams';
  return null;
});

const selectedInterviewerLabel = computed(() => {
  const option = interviewerOptions.value.find(o => o.value === interviewerId.value);
  return option ? `${option.label} ${option.email}` : '';
});

const showSuggestedLabel = computed(() => !isExpanded.value && interviewerOptions.value.length > 0);
</script>
